import 'dayjs/locale/de';
import 'dayjs/locale/en';

import {
  LANGUAGE,
  LANGUAGE_SWITCHER_MODE_TYPE,
  LANGUAGE_TYPE,
  LOCAL_STORAGE_TYPE,
  mappableCompanyRouteKeys,
} from '@utils/constants';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLanguage, setLanguage } from '../../store/slices/configSlice';
import { Divider, Item, LanguageSwitcherRoot } from './styles';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface LanguageItemProps {
  mode: (typeof LANGUAGE_SWITCHER_MODE_TYPE)[number];
}
const LanguageSwitcher = ({ mode }: LanguageItemProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const language = useSelector(getLanguage);
  const location = useLocation();
  const navigate = useNavigate();

  const valueLanguage = window.localStorage.getItem(
    LOCAL_STORAGE_TYPE.LANGUAGE
  ) as (typeof LANGUAGE_TYPE)[number];
  const [hovered, setHovered] = useState<(typeof LANGUAGE_TYPE)[number]>();

  useEffect(() => {
    if (valueLanguage) setHovered(valueLanguage);
  }, [language, valueLanguage]);

  const handleLanguage = useCallback(
    (targetLang: (typeof LANGUAGE_TYPE)[number]) => {
      const currentPath = location.pathname;

      // Find current route key from mappableCompanyRouteKeys
      const matchedKey = mappableCompanyRouteKeys.find(key => {
        const translated = i18n.t(`mappable-company-routes.${key}`);
        return currentPath === translated;
      });

      // Update i18n & Redux & localStorage
      window.localStorage.setItem(LOCAL_STORAGE_TYPE.LANGUAGE, targetLang);
      void i18n.changeLanguage(targetLang);
      dispatch(setLanguage(targetLang));
      dayjs.locale(targetLang);

      // If on a mappable route, redirect to its translated version
      if (matchedKey) {
        const newPath = i18n.t(`mappable-company-routes.${matchedKey}`, {
          lng: targetLang,
        });
        navigate(newPath);
      }
    },
    [dispatch, i18n, location.pathname]
  );

  return (
    <LanguageSwitcherRoot mode={mode} data-testid="language-switcher">
      <Item
        $isHovered={hovered === LANGUAGE.GERMAN}
        onMouseOver={() => {
          setHovered(LANGUAGE.GERMAN);
        }}
        onMouseOut={() => {
          setHovered(language);
        }}
        onClick={() => {
          handleLanguage(LANGUAGE.GERMAN);
        }}
      >
        {t(LANGUAGE.GERMAN).toUpperCase()}
      </Item>
      <Divider />
      <Item
        $isHovered={hovered === LANGUAGE.ENGLISH}
        onMouseOver={() => {
          setHovered(LANGUAGE.ENGLISH);
        }}
        onMouseOut={() => {
          setHovered(language);
        }}
        onClick={() => {
          handleLanguage(LANGUAGE.ENGLISH);
        }}
      >
        {t(LANGUAGE.ENGLISH).toUpperCase()}
      </Item>
    </LanguageSwitcherRoot>
  );
};

export default LanguageSwitcher;
